import { Injectable, inject, DestroyRef } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BreakpointObserverService } from '@lx/shared/utils-dom';
import { KeyboardModeService } from '@lx/shared/ui/keyboard-focus';

@Injectable({ providedIn: 'root' })
export class AutoFocusService {
  private readonly breakpointObserverService = inject(BreakpointObserverService);
  private readonly keyboardModeService = inject(KeyboardModeService);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Focus element when (desktop && autofocus === true)
   * If keyboard mode is active, preserve it during programmatic focus
   */
  focusWhenDesktop(autofocus$: Observable<boolean>, getElement: () => HTMLElement | null) {
    combineLatest([this.breakpointObserverService.desktop$, autofocus$])
      .pipe(
        filter(([desktop, autofocus]) => desktop && autofocus),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const el = getElement();
        if (el) {
          // Check if we're in keyboard mode before focusing
          const keyboardModeActive = this.keyboardModeService.isKeyboardMode;

          setTimeout(() => {
            el.focus();

            // If we were in keyboard mode, ensure it stays active after programmatic focus
            // This is crucial for elements inside panels that open via keyboard
            if (keyboardModeActive) {
              // Force keyboard mode to remain active since this focus is in response to keyboard navigation
              this.keyboardModeService.setKeyboardMode(true);
            }
          }, 10);
        }
      });
  }
}
